import { LoadingButton } from "@mui/lab";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { NotificationChannel } from "@sinch/components/application/Messages/useMessageChannels";
import { FormContainer } from "@sinch/components/form/FormContainer/FormContainer";
import { useGlobalApi } from "@sinch/core/hooks/useGlobalApi";
import { useSnackbar } from "notistack";
import React, { FC, useCallback, useMemo, useState } from "react";
import { useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MessageForm, Placeholders } from "@sinch/components/application/Messages/MessageForm/MessageForm";
import { MessageFormMode, useMessageForm } from "@sinch/components/application/Messages/MessageForm/useMessageForm";
import { ChannelSelect } from "@sinch/views/MessageDialog/ChannelSelect";
import { Box } from "@mui/material";
import { PremiumNoticeAlert } from "@sinch/components/application/common/PremiumNoticeAlert";
import { intersection } from "ramda";
import { MessageFormFields } from "@sinch/components/application/Messages/MessageForm/formSchema";
import { SaveAsTemplateButton } from "@sinch/components/application/Messages/MessageForm/SaveAsTemplateButton";
import { UserRoleEnum } from "@sinch/core/entities/serverEnums";
import { MdsIcon } from "@sinch/components";
import { MdsInfoXs } from "@sinch/icons";
import { JobAudienceFilter } from "@sinch/components/application/Messages/MessageForm/JobAudienceFilter";
import {
  AudienceSelection,
  FULL_AUDIENCE_SELECTION,
  isMixedAudienceUser,
} from "@sinch/components/application/Messages/MessageForm/jobAudience";
import { useJobAudience } from "@sinch/components/application/Messages/MessageForm/useJobAudience";

export interface MessageDialogProps {
  /**
   * Allowed channels for messaging.
   */
  allowedChannels: NotificationChannel[];
  /**
   * Length of the generated reconfirmation link, used in reconfirmation mode.
   */
  reconfirmationLinkLength: number;
  /**
   * Channel that is initially opened/selected when the dialog mounts.
   */
  defaultOpened?: NotificationChannel;
  /**
   * When `true`, hides the channel selector so the channel cannot be changed.
   */
  hideChannelSelect?: boolean;
  /**
   * When `true`, prefills the form with the default message template. Defaults to `true`.
   */
  fillDefaultTemplate?: boolean;
  /**
   * Restricts recipient search to users with one of the given base roles.
   */
  roles?: UserRoleEnum[];
}

interface OpenMessageDialogProps {
  /**
   * List of recipient IDs.
   */
  recipientsIds: string[];
  /**
   * The type of the related object.
   */
  relatedObject: "order" | "job" | "shift" | "position";
  /**
   * ID of the related object.
   */
  relatedObjectId: string;
  /**
   * Initial audience for a job message, missing groups default to everything.
   * Only used when `relatedObject` is `job`.
   */
  audience?: Partial<AudienceSelection>;
}

interface OpenReconfirmationDialogProps {
  /**
   * List of attendance IDs.
   */
  attendanceIds: {
    id: string;
    userId: string;
  }[];
}

/**
 * Component to render a message dialog for sending messages.
 */
export const MessageDialog: FC<MessageDialogProps> = ({
  allowedChannels = [],
  reconfirmationLinkLength,
  defaultOpened,
  hideChannelSelect,
  fillDefaultTemplate = true,
  roles,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<MessageFormMode>(MessageFormMode.Default);
  const [dialogData, setDialogData] = useState<OpenMessageDialogProps | OpenReconfirmationDialogProps | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const messageDialogGlobalApi = useMemo(
    () => ({
      openMessageDialog: (props: OpenMessageDialogProps) => {
        setDialogData(props);
        setMode(MessageFormMode.Default);
        setOpen(true);
      },
      openReconfirmationDialog: (props: OpenReconfirmationDialogProps) => {
        setDialogData(props);
        setMode(MessageFormMode.Reconfirmation);
        setOpen(true);
      },
    }),
    []
  );
  useGlobalApi("MessageDialog", messageDialogGlobalApi);

  /**
   * Callback fired on successful message sending.
   */
  const onSuccess = useCallback((message?: string) => {
    if (message) {
      enqueueSnackbar(message, { variant: "success" });
    }
    setOpen(false);
  }, []);

  /**
   * Callback fired when closing the dialog.
   */
  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  const attendances = (dialogData as OpenReconfirmationDialogProps)?.attendanceIds;
  const messageDialogData = mode === MessageFormMode.Default ? (dialogData as OpenMessageDialogProps | null) : null;
  const audienceJobId =
    messageDialogData?.relatedObject === "job" ? String(messageDialogData.relatedObjectId) : undefined;
  const attendanceIds = useMemo(() => attendances?.map(({ id }) => id), [attendances]);

  const emailPlaceholders = {
    [NotificationChannel.Email]: [
      {
        value: "name",
        label: t("firstName"),
        size: 45,
      },
      {
        value: "surname",
        label: t("lastName"),
        size: 45,
      },
      {
        value: "email",
        label: t("email"),
        size: 80,
      },
    ],
  };

  const { form, onSubmit, placeholders } = useMessageForm({
    recipientsIds: (dialogData as OpenMessageDialogProps)?.recipientsIds,
    relatedObject: (dialogData as OpenMessageDialogProps)?.relatedObject,
    relatedObjectId: (dialogData as OpenMessageDialogProps)?.relatedObjectId,
    onSuccess: () => onSuccess(t("Messages.sent")),
    allowedChannels: allowedChannels,
    reconfirmationLinkLength: reconfirmationLinkLength,
    attendances: attendances,
    mode,
    defaultOpened,
    placeholders: emailPlaceholders,
  });

  return (
    <Dialog fullWidth maxWidth="md" onClose={onClose} open={open}>
      {/*
        TODO: Upgrade react-hook-form to the newest version and remove the key-based remount below
        (the MessageDialogBody extraction can then be inlined back). In react-hook-form 7.50,
        useWatch/useFormState subscribe to the control only on mount, so when `mode` switches the
        form instance returned by useMessageForm, all form hooks in the already-mounted subtree stay
        subscribed to the stale form. Newer react-hook-form re-subscribes when control changes.
      */}
      <FormContainer key={mode} form={form}>
        <MessageDialogBody
          allowedChannels={allowedChannels}
          attendanceIds={attendanceIds}
          audience={messageDialogData?.audience}
          audienceJobId={audienceJobId}
          fillDefaultTemplate={fillDefaultTemplate}
          hideChannelSelect={hideChannelSelect}
          mode={mode}
          onClose={onClose}
          onSubmit={onSubmit}
          placeholders={placeholders}
          roles={roles}
        />
      </FormContainer>
    </Dialog>
  );
};

interface MessageDialogBodyProps {
  allowedChannels: NotificationChannel[];
  attendanceIds?: string[];
  /** Initial audience selection for a job message. */
  audience?: Partial<AudienceSelection>;
  /** When set, the dialog offers the job audience filter for this job. */
  audienceJobId?: string;
  fillDefaultTemplate?: boolean;
  hideChannelSelect?: boolean;
  mode: MessageFormMode;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  placeholders: Partial<Record<NotificationChannel, Placeholders>>;
  roles?: UserRoleEnum[];
}

/**
 * Dialog body rendered inside the form context. Extracted so it can be remounted
 * via `key` when the form instance changes (see TODO above).
 */
const MessageDialogBody: FC<MessageDialogBodyProps> = ({
  allowedChannels,
  attendanceIds,
  audience,
  audienceJobId,
  fillDefaultTemplate,
  hideChannelSelect,
  mode,
  onClose,
  onSubmit,
  placeholders,
  roles,
}) => {
  const { t } = useTranslation();

  const selectedChannel = useWatch<MessageFormFields, "channels">({ name: "channels" });
  const isAllowed = intersection(selectedChannel, allowedChannels).length > 0;

  const { attendances: audienceAttendances, summaries: audienceSummaries } = useJobAudience(audienceJobId);
  const initialAudience = useMemo(() => ({ ...FULL_AUDIENCE_SELECTION, ...audience }), [audience]);

  // People whose attendances on the job fall into different audiences get a
  // hint on their tag, so the admin can decide about them by hand.
  const renderTagHint = useCallback(
    (userId: string) => {
      const summary = audienceSummaries.get(userId);
      if (!summary || !isMixedAudienceUser(summary)) {
        return null;
      }
      const { total, invited, cancelled } = summary;
      const lines: string[] = [];
      if (invited > 0 && invited < total) {
        lines.push(t("MessageDialog.audienceMixedInvited", { invited, total }));
      }
      if (cancelled > 0 && cancelled < total) {
        lines.push(t("MessageDialog.audienceMixedCancelled", { cancelled, total }));
      }

      return (
        <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{lines.join("\n")}</span>}>
          <MdsIcon color="info" fontSize="xs" icon={MdsInfoXs} />
        </Tooltip>
      );
    },
    [audienceSummaries, t]
  );

  return (
    <>
      <DialogTitle>
        {mode === MessageFormMode.Reconfirmation
          ? t("MessageDialog.reconfirmationDialotTitle")
          : t("MessageDialog.dialotTitle")}
      </DialogTitle>
      <DialogContent>
        {!hideChannelSelect && <ChannelSelect />}
        {!isAllowed && (
          <Box sx={{ pb: 3 }}>
            <PremiumNoticeAlert />
          </Box>
        )}
        <MessageForm
          allowedChannels={allowedChannels}
          attendanceIds={attendanceIds}
          fillDefaultTemplate={fillDefaultTemplate}
          mode={mode}
          placeholders={placeholders}
          recipientsInputProps={
            audienceAttendances.length > 0
              ? {
                  renderTagHint,
                  filters: <JobAudienceFilter attendances={audienceAttendances} initialSelection={initialAudience} />,
                }
              : undefined
          }
          roles={roles}
          selectedChannel={selectedChannel}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", alignItems: "center" }}>
        <DialogActionsButtons mode={mode} onClose={onClose} onSubmit={onSubmit} />
      </DialogActions>
    </>
  );
};

const DialogActionsButtons = ({
  mode,
  onClose,
  onSubmit,
}: {
  mode: MessageFormMode;
  onClose: () => void;
  onSubmit: () => Promise<void>;
}) => {
  const { t } = useTranslation();
  const { isValid, isSubmitting } = useFormState<MessageFormFields>();
  return (
    <>
      <SaveAsTemplateButton ignoreSubject={mode === MessageFormMode.Reconfirmation} />
      <Stack direction="row" spacing={1}>
        <Button onClick={onClose}>{t("cancel")}</Button>
        <LoadingButton disabled={!isValid} loading={isSubmitting} onClick={onSubmit} variant="contained">
          {t("Messages.send")}
        </LoadingButton>
      </Stack>
    </>
  );
};
